// apps/client/src/ui/screens/residents.tsx (M2-T08, spec §4.4, §10.1) — the town's residents by move-in date (Menu). Tapping one opens
// their card: Report name and Block for anyone else, Make mayor and Remove for the mayor, Leave town on your own card. The mayor tools
// and Leave sit behind an inline confirm row. The DO's resident.updated / resident.left events refresh the list.
import type { ResidentId, ResidentPublic } from '@whiskerton/shared';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { api } from '../../api/endpoints';
import { HttpError } from '../../api/http';
import { useCommands } from '../../game/instance';
import { safetyBlockedNow, safetyReady } from '../../state/slices/safety';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Panel, Tag } from '../kit';
import type { ScreenDef } from '../router';
import { SafetyButton } from './report';

declare module '../router' { interface ScreenParams { residents: undefined } }

type Pending = 'mayor' | 'remove' | 'leave';
const DATE = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });
const failed = (e: unknown) => useGame.getState().toast(e instanceof HttpError ? (`errors.${e.error}` as const) : 'errors.server_error', undefined, 'warn');

function Card({ r, onLeft }: { r: ResidentPublic; onLeft: () => void }) {
  const t = useT();
  const cmd = useCommands();
  const { townId, town, you, blocked } = useGame(useShallow((s) => ({
    townId: s.townId, town: s.town, you: s.you?.resident ?? null, blocked: r.userId in safetyBlockedNow(s) })));
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  if (!townId || !town) return null;
  const self = r.id === you;
  const mayor = !!you && town.mayor === you;
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try { await work(); } catch (e) { failed(e); } finally { setBusy(false); setPending(null); }
  };
  const toggleBlock = () => run(() => blocked ? useGame.getState().safetyUnblock(r.userId) : useGame.getState().safetyBlock(r.userId, r.name));
  const confirm = () => {
    if (pending === 'mayor') void run(() => api.setMayor(townId, r.id));
    else if (pending === 'remove') void run(() => api.removeResident(townId, r.id));
    else if (pending === 'leave') void run(async () => {
      await api.removeResident(townId, r.id);
      onLeft();
      cmd.leaveTown();
      const s = useGame.getState();
      // drop the town from your town list here, so the towns screen is right without another GET /api/me
      if (s.me) s.setMe({ ...s.me, towns: s.me.towns.filter((x) => x.id !== townId) });
      s.setPhase('towns');
    });
  };
  const question = pending === 'mayor' ? t('safety.makeMayorConfirm', { name: r.name })
    : pending === 'remove' ? t('safety.removeConfirm', { name: r.name, town: town.name })
    : pending === 'leave' ? t('safety.leaveConfirm', { town: town.name }) : null;
  return (
    <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Tag color={r.color}>{r.name}</Tag>
        {r.role === 'mayor' && <span style={{ fontWeight: 900, color: 'var(--leaf-700)' }}>{t('safety.mayor')}</span>}
        {self && <span style={{ fontWeight: 800 }}>{t('safety.you')}</span>}
      </div>
      <p style={{ margin: 0, fontWeight: 800 }}>{t('safety.joined', { date: DATE.format(new Date(r.joinedAt)) })}</p>
      {!self && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SafetyButton testId="report-name" variant="quiet" disabled={busy}
              onClick={() => useGame.getState().openScreen('report', { townId, kind: 'name', refId: r.id, preview: r.name })}>{t('safety.reportName')}</SafetyButton>
            <SafetyButton testId="block-toggle" variant="quiet" disabled={busy} onClick={() => void toggleBlock()}>
              {t(blocked ? 'safety.unblock' : 'safety.block')}
            </SafetyButton>
          </div>
          {blocked && <p style={{ margin: 0, fontSize: 14 }}>{t('safety.blockedNote')}</p>}
        </>
      )}
      {!self && mayor && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <SafetyButton testId="make-mayor" variant="quiet" disabled={busy} onClick={() => setPending('mayor')}>{t('safety.makeMayor')}</SafetyButton>
          <SafetyButton testId="remove-resident" variant="quiet" disabled={busy} onClick={() => setPending('remove')}>{t('safety.remove')}</SafetyButton>
        </div>
      )}
      {self && <SafetyButton testId="leave-town" variant="quiet" disabled={busy} onClick={() => setPending('leave')}>{t('safety.leave')}</SafetyButton>}
      {question && (
        <div role="alertdialog" aria-label={question} style={{ display: 'grid', gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--cream-100)' }}>
          <p style={{ margin: 0, fontWeight: 800 }}>{question}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SafetyButton testId="confirm-action" disabled={busy} onClick={confirm}>{t('safety.confirm')}</SafetyButton>
            <SafetyButton variant="quiet" disabled={busy} onClick={() => setPending(null)}>{t('common.cancel')}</SafetyButton>
          </div>
        </div>
      )}
    </div>
  );
}

function Residents({ close }: { params: undefined; close: () => void }) {
  const t = useT();
  const { residents, presence, town, townId, you } = useGame(useShallow((s) => ({
    residents: s.residents, presence: s.presence, town: s.town, townId: s.townId, you: s.you?.resident ?? null })));
  const [selected, setSelected] = useState<ResidentId | null>(null);
  useEffect(() => {
    if (!safetyReady(useGame.getState())) void useGame.getState().safetyLoad().catch(failed);
  }, []);
  const list = Object.values(residents).sort((a, b) => a.joinedAt - b.joinedAt);
  const current = selected ? residents[selected] : undefined;
  return (
    <Panel title={t('safety.residentsTitle')}>
      <div style={{ display: 'grid', gap: 12, width: 'min(680px, 84cqw)', maxWidth: '100%' }}>
        {town && townId && (
          <div style={{ justifySelf: 'end' }}>
            <SafetyButton variant="quiet"
              onClick={() => useGame.getState().openScreen('report', { townId, kind: 'town_name', refId: townId, preview: town.name })}>{t('safety.reportTownName')}</SafetyButton>
          </div>
        )}
        <div style={{ display: 'grid', gap: '12px 20px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', alignItems: 'start' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {list.map((r) => {
              const here = presence[r.id] && !presence[r.id]!.away;
              return (
                <li key={r.id}>
                  <button type="button" data-testid="resident-row" aria-pressed={selected === r.id} title={here ? t('safety.inTown') : undefined} onClick={() => setSelected(r.id)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: 0, borderRadius: 14, cursor: 'pointer',
                      font: 'inherit', fontWeight: 800, color: 'var(--wood-800)', background: selected === r.id ? 'var(--cream-200)' : 'var(--cream-100)' }}>
                    <Tag color={r.color} dot={!!here}>{r.name}</Tag>
                    {r.role === 'mayor' && <span style={{ fontSize: 14, color: 'var(--leaf-700)' }}>{t('safety.mayor')}</span>}
                    {r.id === you && <span style={{ fontSize: 14, marginLeft: 'auto' }}>{t('safety.you')}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {current && <Card key={current.id} r={current} onLeft={close} />}
        </div>
        <SafetyButton variant="quiet" onClick={close}>{t('common.close')}</SafetyButton>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'residents'> = {
  id: 'residents', layer: 'panel', component: Residents, menu: { label: 'safety.residentsMenu', order: 35, visible: (s) => !!s.townId },
};
